/**
 * Words for a refusal of the receipt API (batch 8 T12c2, #3033).
 *
 * The server's `reason` (and the `reason` of each violation) is a stable code; its `message` is prose the user must never see (it
 * may name ids). Every known code has a FI/EN text under `errors.reason.<code>`; anything else falls back to the status class.
 */

import type { TFunction } from 'i18next';
import { isSessionCancel, ReceivingAccessError, UnexpectedResponseError } from './receivingSession';

export const KNOWN_REASONS = [
  'validation',
  'idempotency_key_required',
  'invalid_idempotency_key',
  'purchase_order_not_found',
  'receipt_not_found',
  'receipt_line_not_found',
  'line_not_found',
  'line_not_on_purchase_order',
  'purchase_order_not_receivable',
  'over_receipt',
  'line_already_received',
  'hold_exceeded',
  'nothing_to_cancel',
  'operation_key_conflict',
  'invalid_line_state',
  'transit_warehouse_missing',
  'counter_overflow',
  'concurrent_update',
  'duplicate_line',
  'no_lines',
  'invalid_input',
  'invalid_quantity',
  'arrived_not_positive',
  'split_does_not_sum',
  'reject_reason_required',
  'reject_reason_invalid',
  'reject_reason_without_rejection',
  'unobserved_with_rejection_or_hold',
  'close_short_reason_required',
] as const;
export type KnownReason = (typeof KNOWN_REASONS)[number];

const isKnown = (value: unknown): value is KnownReason => typeof value === 'string' && (KNOWN_REASONS as readonly string[]).includes(value);

interface ErrorLike {
  status?: unknown;
  data?: unknown;
}

const record = (value: unknown): Record<string, unknown> => (typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {});

/** The status of an API error, or null for anything else (a lost connection, a client refusal). */
export function statusOf(error: unknown): number | null {
  const { status } = (error ?? {}) as ErrorLike;
  return typeof status === 'number' ? status : null;
}

/** The stable reason code of an API refusal, when the server named a known one. */
export function reasonOf(error: unknown): KnownReason | null {
  const reason = record(record(error as ErrorLike).data)['reason'];
  return isKnown(reason) ? reason : null;
}

/** The distinct known violation codes of a refusal, in the server's order. */
export function violationCodes(error: unknown): KnownReason[] {
  const details = record(record(record(error as ErrorLike).data)['details']);
  const violations = details['violations'];
  if (!Array.isArray(violations)) return [];
  const codes: KnownReason[] = [];
  for (const violation of violations as unknown[]) {
    const code = record(violation)['code'];
    if (isKnown(code) && !codes.includes(code)) codes.push(code);
  }
  return codes;
}

/** A refusal or failure, in words the user can act on. `tc` is the common namespace. */
export function describeReceivingError(error: unknown, t: TFunction): string {
  if (error instanceof ReceivingAccessError) return t('errors.noPermission');
  if (error instanceof UnexpectedResponseError) return t('errors.unexpectedResponse');
  if (isSessionCancel(error)) return t('errors.sessionChanged');
  const status = statusOf(error);
  if (status === null) return t('errors.network');
  const reason = reasonOf(error);
  const codes = violationCodes(error).filter((code) => code !== reason);
  if (reason !== null) {
    const main = t(`errors.reason.${reason}`);
    return codes.length > 0 ? `${main} ${codes.map((code) => t(`errors.reason.${code}`)).join(' ')}` : main;
  }
  if (status === 423) return t('errors.locked');
  if (status === 401) return t('errors.sessionExpired');
  if (status === 403) return t('errors.forbidden');
  if (status === 404) return t('errors.notFound');
  if (status === 409) return t('errors.conflict');
  if (status >= 500) return t('errors.server');
  return t('errors.rejected');
}

/**
 * True only when the server decided and nothing was written (or the client refused before sending): a 4xx that is not an
 * auth/session guard, a timeout or "repeat with the same key" (`concurrent_update`, a rolled-back concurrency conflict).
 * Everything else (a lost connection, a 5xx, a lock, an expired token, an unreadable answer) leaves the write's fate open.
 */
export function isDefiniteRefusal(error: unknown): boolean {
  if (error instanceof ReceivingAccessError) return true;
  const status = statusOf(error);
  if (status === null || status < 400 || status >= 500) return false;
  if ([401, 403, 408, 423, 425, 429].includes(status)) return false;
  if (reasonOf(error) === 'concurrent_update') return false;
  const body = record(record(error as ErrorLike).data);
  const unresolved = ['IDEMPOTENCY_IN_PROGRESS', 'IDEMPOTENCY_OUTCOME_UNKNOWN'];
  if (unresolved.includes(String(body['code'])) || unresolved.includes(String(body['error']))) return false;
  return true;
}
